// @ts-check
// Menu "/" e menções "@" do editor de notas (doc 10, seções 7.3 e 7.5): quando abrir, o que buscar e quais blocos
// oferecer. Funções puras; o editor (record-show/rich-text-editor.js) aplica o resultado no contenteditable.

/**
 * @typedef {{ id: string, icon: string, labelKey: string, aliases: string[] }} SlashCommand
 */

/** @type {ReadonlyArray<SlashCommand>} */
export const SLASH_COMMANDS = Object.freeze([
  { id: 'heading1', icon: 'IconH1', labelKey: 'recordPage.slash.heading1', aliases: ['titulo 1', 'h1', 'heading'] },
  { id: 'heading2', icon: 'IconH2', labelKey: 'recordPage.slash.heading2', aliases: ['titulo 2', 'h2', 'subtitulo'] },
  { id: 'heading3', icon: 'IconH3', labelKey: 'recordPage.slash.heading3', aliases: ['titulo 3', 'h3'] },
  { id: 'bulletList', icon: 'IconList', labelKey: 'recordPage.slash.bulletList', aliases: ['lista', 'marcadores', 'bullet'] },
  { id: 'numberedList', icon: 'IconListNumbers', labelKey: 'recordPage.slash.numberedList', aliases: ['lista numerada', 'numeros', 'ordered'] },
  { id: 'checklist', icon: 'IconListCheck', labelKey: 'recordPage.slash.checklist', aliases: ['tarefas', 'checklist', 'todo'] },
  { id: 'quote', icon: 'IconBlockquote', labelKey: 'recordPage.slash.quote', aliases: ['citacao', 'quote'] },
  { id: 'divider', icon: 'IconMinus', labelKey: 'recordPage.slash.divider', aliases: ['divisor', 'linha', 'separador', 'hr'] },
  { id: 'paragraph', icon: 'IconPilcrow', labelKey: 'recordPage.slash.paragraph', aliases: ['paragrafo', 'texto', 'p'] },
]);

const MAXIMUM_MENTION_QUERY_LENGTH = 30;

/** @param {string} text */
export const normalizeCommandText = (text) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * Gatilho ativo no texto antes do cursor: "/" ou "@" no começo ou depois de espaço, seguido do que já foi digitado.
 * O "/" vale só até o primeiro espaço; a menção aceita nome e sobrenome.
 * @param {string} textBeforeCaret texto do nó atual até o cursor
 * @returns {{ trigger: '/' | '@', query: string, offset: number } | null} offset: posição do gatilho no nó
 */
export function detectEditorTrigger(textBeforeCaret) {
  const text = String(textBeforeCaret ?? '').replace(/ /g, ' ');
  for (let index = text.length - 1; index >= 0 && text.length - index <= MAXIMUM_MENTION_QUERY_LENGTH + 1; index -= 1) {
    const character = text[index];
    if (character === '\n') return null;
    if (character !== '/' && character !== '@') continue;
    const isWordStart = index === 0 || /\s/.test(text[index - 1]);
    if (!isWordStart) return null;
    const query = text.slice(index + 1);
    if (character === '/' && /\s/.test(query)) return null;
    if (character === '@' && (/^\s/.test(query) || /\s\s/.test(query))) return null;
    return { trigger: character, query, offset: index };
  }
  return null;
}

/**
 * Comandos que casam com o texto digitado depois do "/" (rótulo traduzido ou apelidos, sem acento).
 * @param {string} query @param {(key: string) => string} translate
 * @returns {SlashCommand[]}
 */
export function filterSlashCommands(query, translate) {
  const normalized = normalizeCommandText(query);
  if (!normalized) return [...SLASH_COMMANDS];
  return SLASH_COMMANDS.filter((command) => [translate(command.labelKey), ...command.aliases]
    .some((candidate) => normalizeCommandText(candidate).split(/\s+/).some((word) => word.startsWith(normalized)) || normalizeCommandText(candidate).startsWith(normalized)));
}

/**
 * Membros que casam com o texto depois do "@" (qualquer parte do nome ou do e-mail, sem acento).
 * @template {{ id: string, name?: { firstName?: string | null, lastName?: string | null } | null, userEmail?: string | null }} TMember
 * @param {TMember[]} members @param {string} query @param {number} [limit]
 * @returns {TMember[]}
 */
export function filterMentionMembers(members, query, limit = 6) {
  const normalized = normalizeCommandText(query);
  return members
    .filter((member) => {
      const haystack = normalizeCommandText(`${member.name?.firstName ?? ''} ${member.name?.lastName ?? ''} ${member.userEmail ?? ''}`);
      return !normalized || normalized.split(/\s+/).every((token) => haystack.includes(token));
    })
    .slice(0, limit);
}
